import { useEffect, useRef, useState } from 'react';
import { Search, Check, Loader2 } from 'lucide-react';
import formStyles from '../styles/form.module.css';
import styles from './AttorneyTypeahead.module.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MIN_QUERY = 2;
const DEBOUNCE_MS = 250;
const LIMIT = 10;

/**
 * Build the search URL.
 *
 * Exported so it can be asserted directly: ApplyNow.js previously called
 * the AUTHENTICATED /lawyers/search from this public page, every request
 * 401'd, and the caller turned that into an empty array — so the typeahead
 * silently found nothing for months. A test pins the endpoint.
 */
export const buildAttorneySearchUrl = (query, limit = LIMIT) =>
  `${API}/lawyers/search-public?q=${encodeURIComponent((query || '').trim())}&limit=${limit}`;

/**
 * AttorneyTypeahead
 *
 * Inline autocomplete over registered attorneys. The dropdown renders ONLY
 * when there are results — there is deliberately no "no matches" state, so
 * with a thin directory the control is indistinguishable from a plain text
 * input and the applicant just types.
 *
 * Selecting links a real lawyer_id and auto-fills the firm. Typing
 * afterwards clears the link, exactly as LawFirmTypeahead does.
 *
 * `onChange` payload: `{ user_id, fullName, lawFirmName, lawFirmPhone, firm_id }`.
 * `firm_id` is forwarded from the matched lawyer record so the application
 * files under the same firm the match displays — without it, a firm typed
 * (and linked) before the attorney match would silently win, filing the app
 * under the wrong firm while showing the matched attorney's firm name.
 */
export const AttorneyTypeahead = ({
  value = '',
  selectedId = '',
  onChange,
  placeholder = "Start typing your attorney's name…",
  hasError = false,
  inputTestId = 'attorney-search',
  dropdownTestId = 'attorney-dropdown',
}) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  // Keep local text in sync when the parent resets the field.
  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const runSearch = async (q) => {
    if (!q || q.trim().length < MIN_QUERY) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await fetch(buildAttorneySearchUrl(q));
      // Do NOT collapse a non-OK response into an empty list silently —
      // that is precisely how the original 401 stayed invisible.
      if (!res.ok) {
        console.error('Attorney search failed:', res.status, res.statusText);
        setResults([]);
        return;
      }
      const data = await res.json();
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Attorney search failed:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleType = (next) => {
    setQuery(next);
    // Typing always means manual entry — drop any linked lawyer.
    onChange?.({ user_id: '', fullName: next, lawFirmName: '', lawFirmPhone: '' });
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSearch(next), DEBOUNCE_MS);
    if (next.trim().length >= MIN_QUERY) setOpen(true);
  };

  const selectLawyer = (lawyer) => {
    setQuery(lawyer.fullName || '');
    setOpen(false);
    onChange?.({
      user_id: lawyer.user_id || '',
      fullName: lawyer.fullName || '',
      lawFirmName: lawyer.lawFirmName || '',
      lawFirmPhone: lawyer.lawFirmPhone || '',
      firm_id: lawyer.firm_id || '',
    });
  };

  return (
    <div ref={wrapRef} className={styles.root}>
      <div className={formStyles.adorned}>
        <Search size={16} className={formStyles.adornedIcon} aria-hidden="true" />
        <input
          id="apply-attorney-name"
          className={`${formStyles.control} ${formStyles.withIcon}${hasError ? ` ${formStyles.controlError}` : ''}`}
          data-testid={inputTestId}
          value={query}
          onChange={(e) => handleType(e.target.value)}
          onFocus={() => { if (query.trim().length >= MIN_QUERY && results.length > 0) setOpen(true); }}
          placeholder={placeholder}
          autoComplete="off"
        />
        {loading && <Loader2 size={16} className={`${formStyles.adornedRight} ${styles.spinner}`} aria-hidden="true" />}
        {!loading && selectedId && (
          <Check
            size={16}
            data-testid="attorney-matched-icon"
            className={`${formStyles.adornedRight} ${formStyles.adornedRightOk}`}
            aria-hidden="true"
          />
        )}
      </div>

      {open && results.length > 0 && (
        <div className={styles.dropdown} data-testid={dropdownTestId}>
          {results.map((l) => (
            <button
              type="button"
              key={l.user_id}
              className={styles.option}
              onClick={() => selectLawyer(l)}
              data-testid={`attorney-option-${l.user_id}`}
            >
              <div className={styles.optionName}>{l.fullName}</div>
              {l.lawFirmName ? <div className={styles.optionMeta}>{l.lawFirmName}</div> : null}
            </button>
          ))}
          <div className={styles.hint}>
            Not listed? Just keep typing your attorney&apos;s name.
          </div>
        </div>
      )}
    </div>
  );
};

export default AttorneyTypeahead;
